import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { z } from "zod";
import { type GovernanceUnitNode } from "~/api/types";
import { updateGovernanceUnit } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import { Input } from "~/atoms/input";
import { governanceUnitNameSchema } from "~/helpers/governanceUnitName";

const FormSchema = z.object({ name: governanceUnitNameSchema });

type FormValues = z.infer<typeof FormSchema>;

interface Props {
  governanceUnit: GovernanceUnitNode | null;
  onClose: () => void;
  onRenamed: () => void;
}

export function RenameGovernanceUnitDialog({
  governanceUnit,
  onClose,
  onRenamed,
}: Props) {
  const { t } = useTranslation();

  return (
    <Dialog open={governanceUnit !== null} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(($) => $.governanceUnits.renameDialog.title)}
          </DialogTitle>
        </DialogHeader>
        {governanceUnit && (
          <RenameForm
            key={governanceUnit.id}
            governanceUnit={governanceUnit}
            onRenamed={() => {
              onRenamed();
              onClose();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface RenameFormProps {
  governanceUnit: GovernanceUnitNode;
  onRenamed: () => void;
}

function RenameForm({ governanceUnit, onRenamed }: RenameFormProps) {
  const { t } = useTranslation();
  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: { name: governanceUnit.name },
  });

  const onSubmit = async ({ name }: FormValues) => {
    try {
      await updateGovernanceUnit(governanceUnit.id, { name });
      toast.success(t(($) => $.governanceUnits.renameSuccess));
      onRenamed();
    } catch {
      toast.error(t(($) => $.governanceUnits.renameError));
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t(($) => $.governanceUnits.name)}</FormLabel>
              <FormControl>
                <Input autoFocus {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting
              ? t(($) => $.commons.buttons.saving)
              : t(($) => $.commons.buttons.save)}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
